import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../../styles/dashboardTheme.css";
import "../../../../styles/managementTheme.css";
import "../styles/interesados.css";
import InteresadosSummary from "../components/InteresadosSummary";
import InteresadosFilters from "../components/InteresadosFilters";
import InteresadosTable from "../components/InteresadosTable";
import InteresadoFormModal from "../components/InteresadoFormModal";
import { usePermissions } from "../../../../utils/permissions";
import { convertirAEstudiante } from "../../../../services/interesadosService";
import { showToast } from "../../../../utils/showToast";

export default function InteresadosView({
  loading,
  error,
  resumen,
  filtros,
  setFiltros,
  limpiarFiltros,
  lastUpdated,
  page,
  pageSize,
  setPageSize,
  totalPages,
  total,
  goToPage,
  interesadosPaginados,
  seleccionado,
  seleccionar,
  reload,
  form,
  fuentesOptions,
  empresasOptions,
}) {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission("interesados.crear");
  const canUpdate = hasPermission("interesados.editar");
  const canDelete = hasPermission("interesados.eliminar");
  const canMatricular = hasPermission("matriculas.crear");
  const navigate = useNavigate();
  const [matriculandoId, setMatriculandoId] = useState(null);

  // Matricular directo desde Interesados: crea el estudiante si aún no existe
  // (no hace falta haber marcado "Pre-inscribir") y abre el formulario de
  // matrícula con ese estudiante ya cargado para elegir la promoción.
  const handleMatricular = async (item) => {
    setMatriculandoId(item.id);
    try {
      let estudianteId = null;
      try {
        const res = await convertirAEstudiante(item.id, { transferToSecretaria: true, prematriculado: true });
        estudianteId = res?.estudiante_id;
      } catch (err) {
        // 409: ya existe un estudiante con esa cédula -> se matricula a ese.
        estudianteId = err?.data?.estudiante_id;
        if (!estudianteId) throw err;
      }
      if (!estudianteId) throw new Error("No se pudo obtener el estudiante.");
      navigate(`/dashboard/matriculas?nuevaParaEstudiante=${estudianteId}`);
    } catch (err) {
      showToast({ type: "danger", message: err?.data?.message || err.message || "No se pudo preparar la matrícula." });
    } finally {
      setMatriculandoId(null);
    }
  };

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (form.modal) document.body.classList.add("modal-open");
    else document.body.classList.remove("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [form.modal]);

  const quickFilter = (key) => {
    switch (key) {
      case "nuevo":
      case "interesado":
      case "prematriculado":
      case "matriculado":
      case "descartado":
      case "pendientes_coordinacion":
        setFiltros((prev) => ({ ...prev, estado: key }));
        break;
      default:
        setFiltros((prev) => ({ ...prev, estado: "all" }));
        break;
    }
  };

  const activeQuickFilter =
    filtros?.estado === "all" || filtros?.estado === "" ? "total" : filtros?.estado || "total";

  const lastSync = lastUpdated ? lastUpdated.toLocaleString() : "Sincronizacion inicial";

  return (
    <section className="management-page management-theme-indigo interesados interesados-wrapper">
      <div className="management-card management-header mb-4">
        <div>
          <p className="management-eyebrow mb-1">Operativo / Interesados</p>
          <h4 className="management-title mb-1 d-flex align-items-center gap-2">
            <i className="bi bi-people" />
            Gestion de interesados
          </h4>
          <p className="text-muted mb-0">Administra los interesados y su recorrido en el proceso.</p>
        </div>
        <div className="interesados-header-actions d-flex flex-column align-items-end gap-2">
          {canCreate && (
            <button type="button" className="btn management-btn-primary" onClick={form.abrirCrear}>
              <i className="bi bi-plus-circle me-2" />
              Nuevo interesado
            </button>
          )}
          <small className="text-muted">Actualizado: {lastSync}</small>
        </div>
      </div>

      <InteresadosSummary resumen={resumen} onQuickFilter={quickFilter} activeKey={activeQuickFilter} />

      <InteresadosFilters
        filtros={filtros}
        setFiltros={setFiltros}
        limpiarFiltros={limpiarFiltros}
        fuentesOptions={fuentesOptions}
        empresasOptions={empresasOptions}
      />

      <InteresadosTable
        interesados={interesadosPaginados}
        loading={loading}
        error={error}
        seleccionadoId={seleccionado?.id}
        onSelect={seleccionar}
        onEdit={canUpdate ? form.abrirEditar : undefined}
        onDelete={canDelete ? form.abrirEliminar : undefined}
        canEdit={canUpdate}
        canDelete={canDelete}
        onMatricular={canMatricular ? handleMatricular : undefined}
        matriculandoId={matriculandoId}
        reload={reload}
      />

      <div className="management-card int-pagination-card d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div className="text-muted small">
          Mostrando {total === 0 ? 0 : (page - 1) * pageSize + 1}-{Math.min(total, page * pageSize)} de {total}
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap int-pagination-controls">
          <select
            className="form-select form-select-sm"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value) || 10);
              goToPage(1);
            }}
          >
            {[10, 25, 50].map((size) => (
              <option key={size} value={size}>
                {size} por pagina
              </option>
            ))}
          </select>
          <div className="btn-group">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1}
            >
              <i className="bi bi-chevron-left" />
            </button>
            <button className="btn btn-outline-secondary btn-sm" disabled>
              Pagina {page} / {totalPages}
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages}
            >
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        </div>
      </div>

      <InteresadoFormModal
        {...form}
        fuentesOptions={fuentesOptions}
        empresasOptions={empresasOptions}
      />
    </section>
  );
}
